import { Suspense, lazy, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation, matchPath } from "react-router-dom";
import { isDespiaNative } from "@/lib/isDespiaNative";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { MainLayout } from "@/components/MainLayout";
import { WorkerLayout } from "@/layouts/WorkerLayout";
import { PeritoLayout } from "@/layouts/PeritoLayout";

import { PeritoCompanySelector } from "@/components/perito/PeritoCompanySelector";
import { aliasDelPerito } from "@/lib/peritoRouteAliases";
import { PeritoEmpresaSinSuscripcion } from "@/components/perito/PeritoEmpresaSinSuscripcion";
import { AccountLoadError } from "@/components/auth/AccountLoadError";
import { PeritoSinEmpresa } from "@/components/perito/PeritoSinEmpresa";
import SubscriptionExpiredPage from "@/pages/SubscriptionExpiredPage";
import { useManagerPermissions, MANAGEABLE_SECTIONS, type SubPermissionKey } from "@/hooks/useManagerPermissions";
import { SEOHead } from "@/components/SEOHead";

// Admin/Manager pages (lazy-loaded)
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const SprayLogs = lazy(() => import("@/pages/SprayLogs"));
const Blocks = lazy(() => import("@/pages/Blocks"));
const GreenhouseDetail = lazy(() => import("@/pages/GreenhouseDetail"));
const DesignerPage = lazy(() => import("@/greenhouse-designer/DesignerPage"));
const Pests = lazy(() => import("@/pages/Pests"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const InventoryDetailPage = lazy(() => import("@/pages/InventoryDetailPage"));
const InventoryPaperwork = lazy(() => import("@/pages/InventoryPaperwork"));
const Planner = lazy(() => import("@/pages/Planner"));

const Analytics = lazy(() => import("@/pages/Analytics"));
const Settings = lazy(() => import("@/pages/Settings"));
const SettingsCompany = lazy(() => import("@/pages/SettingsCompany"));
const SettingsCuaderno = lazy(() => import("@/pages/SettingsCuaderno"));
const SettingsMarca = lazy(() => import("@/pages/SettingsMarca"));
const SettingsStructure = lazy(() => import("@/pages/SettingsStructure"));
const SettingsEstructuraCampos = lazy(() => import("@/pages/SettingsEstructuraCampos"));
const SettingsTeam = lazy(() => import("@/pages/SettingsTeam"));
const SettingsReports = lazy(() => import("@/pages/SettingsReports"));
const SettingsBilling = lazy(() => import("@/pages/SettingsBilling"));
const SettingsDeliveryAddresses = lazy(() => import("@/pages/SettingsDeliveryAddresses"));
const SettingsDataHealth = lazy(() => import("@/pages/SettingsDataHealth"));
const Campaigns = lazy(() => import("@/pages/Campaigns"));
const SettingsRegional = lazy(() => import("@/pages/SettingsRegional"));
const SettingsLegal = lazy(() => import("@/pages/SettingsLegal"));
const SettingsAccount = lazy(() => import("@/pages/SettingsAccount"));
const SettingsMyAccount = lazy(() => import("@/pages/SettingsMyAccount"));

const AdvancedFeatures = lazy(() => import("@/pages/AdvancedFeatures"));
const Teams = lazy(() => import("@/pages/Teams"));
const TeamTerminals = lazy(() => import("@/pages/TeamTerminals"));
const TeamTerminalDetail = lazy(() => import("@/pages/TeamTerminalDetail"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const GettingStartedPage = lazy(() => import("@/pages/GettingStartedPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Tutorials = lazy(() => import("@/pages/Tutorials"));
const CampaignCompare = lazy(() => import("@/pages/CampaignCompare"));
// Analíticas › Reportes (v3): tres informes, mismo guard que /analytics.
const PlagasReport = lazy(() => import("@/pages/reports/PlagasReport"));
const CostesReport = lazy(() => import("@/pages/reports/CostesReport"));
const CampanasReport = lazy(() => import("@/pages/reports/CampanasReport"));
const Shop = lazy(() => import("@/pages/Shop"));
const ShopCart = lazy(() => import("@/pages/ShopCart"));
const ShopOrders = lazy(() => import("@/pages/ShopOrders"));
const ShopOrderDetail = lazy(() => import("@/pages/ShopOrderDetail"));

// Worker pages (lazy-loaded)
const WorkerTodayPage = lazy(() => import("@/pages/worker/WorkerTodayPage"));
const WorkerUpcomingPage = lazy(() => import("@/pages/worker/WorkerUpcomingPage"));
const WorkerTaskDetailPage = lazy(() => import("@/pages/worker/WorkerTaskDetailPage"));
// El candado NO va en lazy: es quien decide si se puede estar en esta pantalla,
// así que tiene que estar montado antes de que se pinte ninguna.
import { WorkerTaskLock } from "@/components/routing/WorkerTaskLock";
const WorkerScoutingListPage = lazy(() => import("@/pages/worker/WorkerScoutingListPage"));
const WorkerNewScoutingPage = lazy(() => import("@/pages/worker/WorkerNewScoutingPage"));
const WorkerProfilePage = lazy(() => import("@/pages/worker/WorkerProfilePage"));

// Perito pages (lazy-loaded)
const PeritoInicio = lazy(() => import("@/pages/perito/PeritoInicio"));
const PeritoPlanner = lazy(() => import("@/pages/perito/PeritoPlanner"));
// PeritoTratamientos merged into PeritoSanidad — route redirects to /perito/sanidad?tab=recetas
const PeritoSanidad = lazy(() => import("@/pages/perito/PeritoSanidad"));
const PeritoProfile = lazy(() => import("@/pages/perito/PeritoProfile"));
const PeritoInventario = lazy(() => import("@/pages/perito/PeritoInventario"));
const PeritoSeguimiento = lazy(() => import("@/pages/perito/PeritoSeguimiento"));

// Route/auth fallbacks are intentionally invisible so the removed startup
// loader can never flash before the WelcomeBackScreen.
const LoadingScreen = () => null;
const SilentFallback = () => null;

/**
 * Redirección que NO pierde la `?`. `<Navigate to="/x" />` a secas descarta la
 * cadena de consulta y el ancla, y hay avisos que entran por ahí (el del panel
 * abre «/campaigns?new=cycles» para desplegar el creador de ciclos).
 */
function RedirectKeepingQuery({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

/**
 * El comodín del portal del perito: una ruta de admin con equivalente va a la
 * pantalla del perito CONSERVANDO la `?` (el «Ver tarea» de la voz, el «Crear
 * tratamiento» del muestreo, los avisos viejos…); lo demás, al Inicio.
 * Auditoría del portal del perito, A20/T04-02, T15-02, T15-03 y M10.
 */
function PeritoFallback() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={aliasDelPerito(pathname, search, hash) ?? "/perito/inicio"} replace />;
}

function ManagerGuard({ children, path }: { children: React.ReactNode; path: string }) {
  const { role } = useAuth();
  const { isRouteAllowed, permissions, loading } = useManagerPermissions();

  if (role === "manager" && !loading && !isRouteAllowed(path)) {
    const firstAllowed = MANAGEABLE_SECTIONS.find(s => permissions[s.key] !== false);
    return <Navigate to={firstAllowed?.route || "/settings"} replace />;
  }

  return <>{children}</>;
}

/**
 * La facturación es del ADMIN y de nadie más (usuario 24-09-2026).
 *
 * Antes era un permiso que el admin podía conceder (`settingsBilling`), y eso
 * es justo lo que no puede ser: ahí se cambia el plan de la empresa —de starter
 * a professional a enterprise— y se paga. No se concede: se tiene por ser
 * admin. El interruptor se ha retirado de las fichas de miembro.
 */
function SoloAdminGuard({ children }: { children: React.ReactNode }) {
  const { role } = useAuth();

  if (role !== "admin") {
    return <Navigate to="/settings" replace />;
  }

  return <>{children}</>;
}

export function RoleBasedRouter() {
  const { user, role, loading, subscriptionExpired, profile, company, linkedCompanies, switchCompany, isFirstAdmin, peritoSinEmpresa } = useAuth();
  // Defensive: when AuthContext says no user but Supabase actually has a
  // session in localStorage (race condition right after /auth-bridge),
  // hold on the loading screen for a beat instead of bouncing to /app-auth
  // and flashing NotFound.
  const [sessionTransition, setSessionTransition] = useState(false);

  /* La pantalla que se pinta DETRÁS de la ficha de una tarea. Los hooks van aquí
     arriba, antes de cualquier `return`, porque debajo hay varias salidas por rol
     y un hook no puede quedarse a medias. Ver el comentario de las rutas del
     trabajador para el porqué. */
  const location = useLocation();
  const workerBackground =
    ((location.state as { backgroundLocation?: typeof location } | null)?.backgroundLocation ??
      (matchPath("/worker/tasks/:id", location.pathname)
        ? ({ ...location, pathname: "/worker/today", search: "", state: null } as typeof location)
        : null));

  useEffect(() => {
    let cancelled = false;
    if (!loading && !user) {
      supabase.auth.getSession().then(({ data }) => {
        if (cancelled) return;
        if (data.session?.user) {
          setSessionTransition(true);
          // Safety: release after 3s so we never get stuck.
          setTimeout(() => !cancelled && setSessionTransition(false), 3000);
        }
      });
    } else if (user && sessionTransition) {
      setSessionTransition(false);
    }
    return () => {
      cancelled = true;
    };
  }, [loading, user, sessionTransition]);

  if (loading || sessionTransition) {
    return <LoadingScreen />;
  }

  if (!user) {
    const authRoute = isDespiaNative() ? '/app-auth' : '/auth';
    return <Navigate to={authRoute} replace />;
  }

  // Users mid-payment (never paid yet) must NEVER see the SubscriptionExpiredPage —
  // that page is for accounts that DID pay and expired. This holds on ANY platform:
  // the same account can finish the signup in the native app or in a browser, so a
  // pending_native_payment / native_free-unpaid company always converges on
  // /app-auth/complete-registration (cold-start + sign-in + post-onboarding).
  if (
    company &&
    (company.subscription_status === "pending_native_payment" ||
      ((company as any).registration_origin === "native_free" &&
        !["active", "past_due", "trial", "trialing"].includes(company.subscription_status || "")))
  ) {
    return <Navigate to="/app-auth/complete-registration" replace />;
  }

  /* El perito no paga la empresa de un cliente: si esa caduca, pasa a otra de
     las suyas o se le explica, sin botones de pago (tanda 5, M40 / T15-06). */
  if (subscriptionExpired && role === "perito") {
    return <PeritoEmpresaSinSuscripcion />;
  }

  if (subscriptionExpired) {
    return <SubscriptionExpiredPage />;
  }

  /* Sin rol, ningún portal (auditoría V1-OBS). Con la carga del rol fallida
     —sin cobertura al abrir, un 500— `role` se quedaba en null y esto caía
     abajo, en la rama del ADMIN: un perito veía el menú de Equipo, Ajustes y
     Costes. Ahora se dice que no se ha podido cargar la cuenta y se reintenta. */
  if (!role) {
    /* El perito al que su única empresa desvinculó (P3): cuenta sin empresa ni
       rol. No es un fallo de carga: se le dice que pida un enlace y lo canjea. */
    if (peritoSinEmpresa) return <PeritoSinEmpresa />;
    return <AccountLoadError />;
  }


  // Onboarding (welcome + intent screens) is shown ONLY to the first admin
  // of the company. Workers, peritos, managers, and additional admins go
  // straight to their normal home page regardless of their stored flags.
  const onboardingEligible = role === "admin" && isFirstAdmin;

  // Paid accounts (active/trialing/etc) MUST NOT be sent back to the
  // onboarding questions just because the intent flag is stale. They may only
  // be shown the welcome screen (first time) before going to their dashboard.
  const companyIsPaid =
    !!company &&
    ["active", "past_due", "trial", "trialing"].includes(
      (company.subscription_status as string) || ""
    );

  if (onboardingEligible && profile && !profile.has_seen_welcome) {
    return <Navigate to="/welcome?from=first-login" replace />;
  }

  if (
    onboardingEligible &&
    profile &&
    !profile.has_completed_onboarding_intent &&
    !companyIsPaid
  ) {
    return <Navigate to="/onboarding/setup" replace />;
  }

  // Worker role - show Worker Portal
  if (role === "worker") {
    return (
      <>
        <SEOHead title="Portal de trabajador | Ulture Pest" description="Portal de trabajo de Ulture Pest." noIndex />
        {/* Con una tarea en marcha no se sale de su ficha: ver `WorkerTaskLock`.
            Envuelve a TODAS las rutas del trabajador porque por aquí pasan
            también el enlace de una notificación y el arranque en frío. */}
        <WorkerTaskLock>
        <Suspense fallback={<SilentFallback />}>
        {/* La ficha de una tarea es la ÚNICA ruta de la app que es en realidad una
            hoja: se abría encima de nada y detrás solo se veía el gris del fondo.
            Con `backgroundLocation` las rutas de abajo siguen pintando la pantalla
            de la que se viene y la ficha se monta aparte, encima.

            El `?? { pathname: "/worker/today" }` es la respuesta al enlace de una
            notificación y al arranque en frío: ahí no hay pantalla de la que
            venir, así que detrás se pinta «Tareas de Hoy» en vez del gris. Y como
            en los dos casos `background` acaba siendo distinto de null, las rutas
            de abajo NUNCA casan con /worker/tasks/:id: la ficha se monta una sola
            vez y sus consultas no salen por duplicado. */}
        <Routes location={workerBackground ?? location}>
          <Route element={<WorkerLayout />}>
            <Route path="/worker/today" element={<WorkerTodayPage />} />
            <Route path="/worker/upcoming" element={<WorkerUpcomingPage />} />
            <Route path="/worker/tasks/:id" element={<WorkerTaskDetailPage />} />
            <Route path="/worker/scouting" element={<WorkerScoutingListPage />} />
            <Route path="/worker/scouting/new" element={<WorkerNewScoutingPage />} />
            <Route path="/worker/profile" element={<WorkerProfilePage />} />
          </Route>
          <Route path="*" element={<Navigate to="/worker/today" replace />} />
        </Routes>
        {workerBackground && (
          <Routes>
            <Route path="/worker/tasks/:id" element={<WorkerTaskDetailPage />} />
          </Routes>
        )}
        </Suspense>
        </WorkerTaskLock>
      </>
    );
  }

  // Perito role - show Perito Portal
  if (role === "perito") {
    // Defensive: if linkedCompanies hasn't hydrated yet, hold instead of
    // falling through to a blank screen.
    if (linkedCompanies.length === 0 && !company) {
      return <LoadingScreen />;
    }

    // If perito has multiple companies and just logged in, show selector
    const hasMultipleCompanies = linkedCompanies.length > 1;
    const needsCompanySelection = hasMultipleCompanies && localStorage.getItem('perito_company_selected') !== 'true';

    if (needsCompanySelection) {
      return (
        <PeritoCompanySelector
          companies={linkedCompanies}
          currentCompanyId={profile?.company_id || null}
          onSelect={async (companyId) => {
            await switchCompany(companyId);
            localStorage.setItem('perito_company_selected', 'true');
          }}
        />
      );
    }

    return (
      <>
        <SEOHead title="Portal de Perito | Ulture Pest" description="Portal técnico de perito de Ulture Pest." noIndex />
        <Suspense fallback={<SilentFallback />}>
        <Routes>
          <Route element={<PeritoLayout />}>
            <Route path="/perito/inicio" element={<PeritoInicio />} />
            <Route path="/perito/planificador" element={<PeritoPlanner />} />
            <Route path="/perito/tratamientos" element={<Navigate to="/perito/sanidad?tab=recetas" replace />} />
            <Route path="/perito/sanidad" element={<PeritoSanidad />} />
            <Route path="/perito/seguimiento" element={<PeritoSeguimiento />} />
            <Route path="/perito/inventario" element={<PeritoInventario />} />
            <Route path="/perito/inventario/:id" element={<InventoryDetailPage />} />
            <Route path="/perito/perfil" element={<PeritoProfile />} />
            {/* Destino de «Ver todas» en la ventana de perfil: la misma página de
                notificaciones que usan admin y manager. */}
            <Route path="/perito/notificaciones" element={<Notifications />} />
          </Route>
          <Route path="*" element={<PeritoFallback />} />
        </Routes>
        </Suspense>
      </>
    );
  }

  // Admin/Manager role - single persistent layout
  return (
    <>
    <SEOHead title="Panel de gestión | Ulture Pest" description="Panel de gestión de Ulture Pest." noIndex />
    <Suspense fallback={<SilentFallback />}>
    <Routes>
      <Route element={<MainLayout />}>
        <Route path="/dashboard" element={<ManagerGuard path="/dashboard"><DashboardPage /></ManagerGuard>} />
        <Route path="/spray-logs" element={<ManagerGuard path="/spray-logs"><SprayLogs /></ManagerGuard>} />
        <Route path="/blocks" element={<ManagerGuard path="/blocks"><Blocks /></ManagerGuard>} />
        <Route path="/blocks/:id" element={<ManagerGuard path="/blocks"><GreenhouseDetail /></ManagerGuard>} />
        <Route path="/planner" element={<ManagerGuard path="/planner"><Planner /></ManagerGuard>} />
        <Route path="/tasks" element={<Navigate to="/planner" replace />} />
        <Route path="/pests" element={<ManagerGuard path="/pests"><Pests /></ManagerGuard>} />
        <Route path="/inventory" element={<ManagerGuard path="/inventory"><Inventory /></ManagerGuard>} />
        <Route path="/inventory/invoices" element={<ManagerGuard path="/inventory"><InventoryPaperwork /></ManagerGuard>} />
        {/* «Auditoría de duplicados» era una subpágina aparte y ahora es una
            pestaña de la de arriba. La ruta se queda como redirección: hay
            enlaces guardados y gente con la URL en el historial. */}
        <Route path="/inventory/duplicates" element={<Navigate to="/inventory/invoices" replace />} />
        <Route path="/inventory/:id" element={<ManagerGuard path="/inventory"><InventoryDetailPage /></ManagerGuard>} />
        <Route path="/consumption" element={<Navigate to="/inventory" replace />} />
        <Route path="/teams" element={<ManagerGuard path="/teams"><Teams /></ManagerGuard>} />
        {/* «Terminal» (30-10-2026): las tablets de la finca, dentro de Equipo. Misma llave. */}
        <Route path="/teams/terminal" element={<ManagerGuard path="/teams"><TeamTerminals /></ManagerGuard>} />
        <Route path="/teams/terminal/:id" element={<ManagerGuard path="/teams"><TeamTerminalDetail /></ManagerGuard>} />
        <Route path="/analytics" element={<ManagerGuard path="/analytics"><Analytics /></ManagerGuard>} />
        <Route path="/advanced" element={<ManagerGuard path="/advanced"><AdvancedFeatures /></ManagerGuard>} />
        {/* Campañas vive ahora bajo Ajustes. Las direcciones viejas siguen
            funcionando: redirigen CONSERVANDO la parte de la ? — el aviso del
            panel entra por «/campaigns?new=cycles» y un Navigate a secas se
            comería el parámetro y abriría la página sin el creador de ciclos. */}
        <Route path="/campaigns" element={<RedirectKeepingQuery to="/settings/campaigns" />} />
        <Route path="/campaigns/compare" element={<RedirectKeepingQuery to="/settings/campaigns/compare" />} />
        <Route path="/reports" element={<Navigate to="/analytics" replace />} />
        <Route path="/reports/plagas" element={<ManagerGuard path="/analytics"><PlagasReport /></ManagerGuard>} />
        <Route path="/reports/costes" element={<ManagerGuard path="/analytics"><CostesReport /></ManagerGuard>} />
        <Route path="/reports/campanas" element={<ManagerGuard path="/analytics"><CampanasReport /></ManagerGuard>} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/getting-started" element={<GettingStartedPage />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/company" element={<SettingsCompany />} />
        {/* «📒 Cuaderno de campo» (27-09-2026): lo que el cuaderno pide una vez, en su
            página. Se entra desde Ajustes → Mi cuenta (debajo de «Idioma y región»,
            desde el 27-09 por la tarde); admin edita, encargado lee.
            Solo existe en este router: el trabajador y el perito no llegan ni por enlace. */}
        <Route path="/settings/cuaderno" element={<SettingsCuaderno />} />
        {/* «🎨 Personalizar informes» (29-09-2026): logo, colores y cabecera de los informes de
            la empresa. Solo el admin; se entra desde Mi cuenta, debajo del cuaderno. */}
        <Route path="/settings/marca" element={<SoloAdminGuard><SettingsMarca /></SoloAdminGuard>} />
        <Route path="/settings/structure" element={<SettingsStructure />} />
        {/* «Campos y numeración» de la estructura (23-10-2026): códigos, números por campaña o
            año y datos de fincas, naves, sectores y cultivos que salen en los informes. Solo el admin. */}
        <Route path="/settings/structure/campos" element={<SoloAdminGuard><SettingsEstructuraCampos /></SoloAdminGuard>} />
        {/* Asistente geométrico de invernaderos (T4 plan Tactic→Pest) */}
        <Route path="/designer" element={<DesignerPage />} />
        <Route path="/designer/:draftId" element={<DesignerPage />} />
        {/* La MISMA llave que «/teams» (usuario 24-09-2026): son la misma pantalla
            por dos puertas, y esta se quedó sin guardia. Un encargado con Equipo
            apagado entraba por Ajustes → Equipo y Roles y veía la plantilla
            entera. */}
        <Route path="/settings/team" element={<ManagerGuard path="/teams"><SettingsTeam /></ManagerGuard>} />
        <Route path="/settings/billing" element={<SoloAdminGuard><SettingsBilling /></SoloAdminGuard>} />
        <Route path="/settings/reports" element={<SettingsReports />} />
        <Route path="/settings/export" element={<Navigate to="/settings/reports" replace />} />
        <Route path="/settings/delivery-addresses" element={<SettingsDeliveryAddresses />} />
        <Route path="/settings/data-health" element={<SettingsDataHealth />} />
        <Route path="/settings/campaigns" element={<ManagerGuard path="/settings/campaigns"><Campaigns /></ManagerGuard>} />
        <Route path="/settings/campaigns/compare" element={<ManagerGuard path="/settings/campaigns"><CampaignCompare /></ManagerGuard>} />
        <Route path="/settings/regional" element={<SettingsRegional />} />
        <Route path="/settings/legal" element={<SettingsLegal />} />
        <Route path="/settings/account" element={<SettingsAccount />} />
        <Route path="/settings/my-account" element={<SettingsMyAccount />} />
        {/* El conector de ChatGPT (MCP) se retiró el 24-09-2026: nadie lo usaba. */}
        <Route path="/settings/integrations" element={<Navigate to="/settings/my-account" replace />} />

        <Route path="/tutorials" element={<Tutorials />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/shop/cart" element={<ShopCart />} />
        <Route path="/shop/orders" element={<ShopOrders />} />
        <Route path="/shop/orders/:id" element={<ShopOrderDetail />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
    </Suspense>
    </>
  );
}
